import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {Machine} from '../../../model/machine';
import {MachineFilters} from '../../../model/filters/machine-filters';
import {MachinesService} from '../../../services/machines.service';
import {TimezoneService} from '../../../services/timezone.service';
import * as moment from 'moment';
import {ColInfo} from 'xlsx';
import * as FileSaver from 'file-saver';
import {FileExtensionType} from '../../../model/file-extension-type';
import {TranslocoService} from '@ngneat/transloco';
import {AuthGuard} from '../../../common/auth-guard.service';
import {AlarmFilters} from '../../../model/filters/alarm-filters';
import {Alarm} from '../../../model/alarm';
import {AlarmsService} from '../../../services/alarms.service';
import {AnalysisCompaniesFilters} from '../../../model/filters/analysis-companies-filters';
import {AuditFilters} from '../../../model/filters/audit-filters';
import {Audit} from '../../../model/audit-model';
import {AuditsService} from '../../../services/audits.service';
import {MachineAnalysis} from '../../../model/machine-analysis';
import {MachinePeriodDataInfo} from '../../../model/custom/machine/machine-period-data-info';
import {MachineLastValues} from '../../../model/custom/machine/machine-last-values';
import {AlarmTelemetryData} from '../../../model/alarm-telemetry-data';
import {MachineGeneralInfo} from '../../../model/custom/machine/machine-general-info';
import {ConsumptionData} from '../../../model/consumption-data';
import {ExportFileType} from "../../../model/export-file-type";

export type TypeContent = 'MACHINE' | 'ALARM' | 'AUDIT' | 'ORGANIZATION' | 'MACHINE_ANALYSIS' | 'CONSUMPTION' |
  'MACHINE_PARK' | 'MACHINE_GENERAL' | 'MACHINE_DATA' | 'MACHINE_CHARTS';

@Component({
  selector: 'app-export-xls',
  templateUrl: './export-xls.component.html',
  styleUrls: ['./export-xls.component.scss']
})
export class ExportXlsComponent implements OnInit {

  @Input() content: Machine[] | Alarm[] | any[];
  @Input() machineFilters: MachineFilters;
  @Input() alarmFilters: AlarmFilters;
  @Input() auditFilters: AuditFilters;
  @Input() organizationFilters: AnalysisCompaniesFilters;
  @Input() categories: string[];
  @Input() machineSerialNumber: string;
  @Input() machineUuid: string;
  @Input() typeContent: TypeContent;
  @Input() exportFileType: ExportFileType;
  @Input() first: number;
  @Input() typeExport: 'XLSX' | 'CSV';
  @Output() visibleChange: EventEmitter<boolean>;

  public dialogVisible: boolean;
  public contentTypeExport: string = 'currentPage';
  public fileName: string;
  public loadingContent: boolean = false;

  private readonly LAST_DAY: string = 'LAST_DAY';
  private readonly LAST_WEEK: string = 'LAST_WEEK';
  private readonly LAST_YEAR: string = 'LAST_YEAR';
  private readonly EMPTY_DATA: string = 'N/A';

  private language: string;
  private viewInLiters: boolean;
  private filtersViewInLiters: boolean;


  constructor(private timezoneService: TimezoneService,
              public authGuard: AuthGuard,
              private i18n: TranslocoService,
              private machinesService: MachinesService,
              private alarmsService: AlarmsService,
              private auditsService: AuditsService) {
    this.visibleChange = new EventEmitter<boolean>();
  }

  ngOnInit(): void {
    this.language = this.i18n.getActiveLang();
  }

  @Input()
  set visible(visible: boolean) {
    this.dialogVisible = visible;
    moment.tz(this.timezoneService.currentTimezone);
  }

  exportAllContentToExcel(): void {
    const promises: any[] = [];
    let promise: any;
    switch (this.typeContent) {
      case 'MACHINE':
        promise = this.machinesService.getMachinesExcel(this.machineFilters, this.fileName.trim() + '.xlsx').toPromise();
        break;
      case 'ALARM':
        promise = this.alarmsService.getAlarmsExcelByMachineSerial(this.machineSerialNumber, this.alarmFilters, this.fileName.trim() + '.xlsx').toPromise();
        break;
      case 'AUDIT':
        promise = this.auditsService.getMachineAuditsExcel(this.machineSerialNumber, this.auditFilters, this.fileName.trim() + '.xlsx').toPromise();
        break;
    }
    promises.push(promise);
    Promise.all(promises).then(response => {
      this.saveAsFile(response[0], this.fileName, FileExtensionType.EXCEL);
    });
  }

  exportCurrentContentToExcel(): void {
    import('xlsx').then(xlsx => {
      this.i18n.selectTranslation().subscribe(() => {
        switch (this.typeContent) {
          case 'MACHINE':
            this.exportMachines(xlsx);
            break;
          case 'MACHINE_GENERAL':
            this.exportMachineGeneral(xlsx);
            break;
          case 'MACHINE_DATA':
            this.exportMachineData(xlsx);
            break;
          case 'MACHINE_CHARTS':
            this.exportMachineCharts(xlsx);
            break;
          case 'ALARM':
            this.exportAlarms(xlsx);
            break;
          case 'AUDIT':
            this.exportAudits(xlsx);
            break;
          case 'MACHINE_ANALYSIS':
            this.exportAnalysisGeneral(xlsx);
            break;
          case 'ORGANIZATION':
            this.exportOrganizations(xlsx);
            break;
          case 'CONSUMPTION':
            this.exportAnalysisConsumption(xlsx);
            break;
          case 'MACHINE_PARK':
            this.exportMachinePark(xlsx);
            break;
        }
      });
    });
  }

  private exportMachines(xlsx: any): void {
    let contentToExport: any[] = [];
    this.content.forEach(machine => contentToExport.push(this.buildMachineData(machine)));
    this.createSheet(contentToExport, xlsx);
  }

  private exportMachineGeneral(xlsx: any): void {
    let contentToExport: any[][] = [[], [], [], [], [], [], [], [], []];
    this.viewInLiters = this.content[7];
    contentToExport[0].push(this.buildMachineGeneralUpperData(this.content[0]));
    contentToExport[1].push(this.buildMachineGeneralValuesData(this.content[0], this.content[1], this.content[6]));
    contentToExport[2].push(this.buildMachineGeneralInfoData(this.content[0]));
    if (!this.authGuard.isECommerce() || this.content[0].subscription?.active) {
      contentToExport[3].push(this.buildMachineGeneralStatusData(this.content[0], this.content[1]));
      contentToExport[4].push(this.buildMachineGeneralStatusButtons(this.content[0]));
      if (this.authGuard.hasPermission('PERMISSION_VIEW_ALARMS')) {
        this.content[2].forEach(activeAlarm => contentToExport[5].push(this.buildMachineActiveAlarmsData(activeAlarm)));
      }
      this.content[5].data?.forEach(alarmDistribution => contentToExport[8].push(this.buildAlarmDistributionData(alarmDistribution)));
    }
    this.content[3].data?.forEach((valuesByHour, index) => contentToExport[6].push(this.buildMachineLastValuesCharts(this.content[3].categories[index], valuesByHour, this.LAST_DAY)));
    this.content[4]?.forEach(squeezedValues => contentToExport[7].push(this.buildValuesMeasurement(squeezedValues, this.content[7])));
    this.createMachineGeneralSheet(contentToExport, xlsx);
  }

  private exportMachineData(xlsx: any): void {
    if (this.exportFileType === ExportFileType.MACHINE_DATA_CHART) {
      this.exportMachineDataSignals(xlsx);
    } else if (this.exportFileType === ExportFileType.MACHINE_DATA) {
      this.exportMachineDataLastValuesPeriodData(xlsx);
    }
  }

  private exportMachineDataSignals(xlsx: any): void {
    let contentToExport: any[] = [];
    this.content.forEach(signal => {
      signal.data.forEach(signalValues => contentToExport.push(this.buildSignalData(signal.name, signalValues)))
    });
    this.createSheet(contentToExport, xlsx);
  }

  private exportMachineDataLastValuesPeriodData(xlsx: any): void {
    let contentToExport: any[] = [];
    this.viewInLiters = this.content[3];
    this.filtersViewInLiters = this.content[4];
    contentToExport.push(this.buildMachineLastValuesData(this.content[0]));
    contentToExport.push(this.buildTimePeriod(this.content[2]));
    contentToExport.push(this.buildMachinePeriodData(this.content[1]));
    this.createMachineDataSheet(contentToExport, xlsx);
  }

  private exportMachineCharts(xlsx: any): void {
    if (this.exportFileType === ExportFileType.MACHINE_CHARTS) {
      this.exportLastValuesCharts(xlsx);
    } else if (this.exportFileType === ExportFileType.MACHINE_CHARTS_IN_PERIOD) {
      this.exportLastValuesPeriodCharts(xlsx);
    }
  }

  private exportLastValuesCharts(xlsx: any): void {
    let contentToExport: any[][] = [[], [], []];
    this.viewInLiters = this.content[3];
    this.content[0].data?.forEach((valuesByHour, index) =>
      contentToExport[0].push(this.buildMachineLastValuesCharts(this.content[0].categories[index], valuesByHour, this.LAST_DAY)));
    this.content[1].data?.forEach((valuesByWeek, index) =>
      contentToExport[1].push(this.buildMachineLastValuesCharts(this.content[1].categories[index], valuesByWeek, this.LAST_WEEK)));
    this.content[2].data?.forEach((valuesByYear, index) =>
      contentToExport[2].push(this.buildMachineLastValuesCharts(this.content[2].categories[index], valuesByYear, this.LAST_YEAR)));
    this.createMachineChartsSheet(contentToExport, xlsx);
  }

  private exportLastValuesPeriodCharts(xlsx: any): void {
    let contentToExport: any[][] = [[], [], []];
    this.filtersViewInLiters = this.content[4];
    contentToExport[0].push(this.buildTimePeriod(this.content[3]));
    contentToExport[1].push(this.buildMachineLastValuesPeriodChartsData(this.content[0], this.content[1]));
    this.content[2].forEach(totalSqueezedMeasurement =>
      contentToExport[2].push(this.buildValuesMeasurement(totalSqueezedMeasurement, this.content[4])));
    this.createMachineChartsPeriodSheet(contentToExport, xlsx);
  }

  private exportAlarms(xlsx: any): void {
    let contentToExport: any[] = [];
    if (this.exportFileType === ExportFileType.MACHINE_ALARMS_LIST) {
      this.content.forEach(alarm => contentToExport.push(this.buildAlarmData(alarm)));
    } else if (this.exportFileType === ExportFileType.MACHINE_ALARMS_TIME_ON_VS_OFF_CHART) {
      this.content.forEach(onVsOff => contentToExport.push(this.buildOnVsOffData(onVsOff)));
    } else if (this.exportFileType === ExportFileType.MACHINE_ALARMS_TIME_CHART) {
      this.content.forEach(alarmedVsNotAlarmed =>
        contentToExport.push(this.buildAlarmedVsNotAlarmedData(alarmedVsNotAlarmed)));
    } else if (this.exportFileType === ExportFileType.MACHINE_ALARMS_DISTRIBUTION_CHART) {
      this.content.forEach(alarmDistribution =>
        contentToExport.push(this.buildAlarmDistributionData(alarmDistribution)));
    }
    this.createSheet(contentToExport, xlsx);
  }

  private exportAudits(xlsx: any): void {
    let contentToExport: any[] = [];
    this.content.forEach(audit => contentToExport.push(this.buildAuditData(audit)));
    this.createSheet(contentToExport, xlsx);
  }

  private exportAnalysisGeneral(xlsx: any): void {
    let contentToExport: any[] = [];
    contentToExport.push(this.buildMachineAnalysisConsumptionData(this.content[0]));
    this.content[1].forEach(machine => contentToExport.push(this.buildMachineAnalysisData(machine)));
    this.createAnalysisGeneralSheet(contentToExport, xlsx);
  }

  private exportOrganizations(xlsx: any): void {
    let contentToExport: any[] = [];
    Object.entries(this.content).forEach(([, organization]) =>
      contentToExport.push(this.buildOrganizationData(organization.data)));
    this.createSheet(contentToExport, xlsx);
  }

  private exportAnalysisConsumption(xlsx: any): void {
    if (this.exportFileType === ExportFileType.ANALYSIS_CONSUMPTION_AND_WORK_CHART) {
      this.exportAnalysisTotalSqueezedFruits(xlsx);
    } else if (this.exportFileType === ExportFileType.ANALYSIS_CONSUMPTION_AND_WORK) {
      this.exportAnalysisAllWidgets(xlsx);
    }
  }

  private exportAnalysisTotalSqueezedFruits(xlsx: any): void {
    let contentToExport: any[] = [];
    this.content.forEach(machine => {
      machine.data.forEach(fruitsMachine => contentToExport.push(this.buildFruitsMachineData(machine.name, fruitsMachine)))
    });
    this.createSheet(contentToExport, xlsx);
  }

  private exportAnalysisAllWidgets(xlsx: any): void {
    let contentToExport: any[][] = [[], [], [], [], [], [], []];
    contentToExport[0].push(this.buildTimePeriod(this.content[0]));
    if (this.content[1] != null) {
      contentToExport[1].push(this.buildConsumptionAndWorkLastValues(this.content[1]));
      contentToExport[2].push(this.buildConsumptionAndWorkData(this.content[1]));
      contentToExport[3].push(this.buildConsumptionAndWorkData2(this.content[1]));
    }
    this.content[2].data?.forEach(machineData => {
      machineData.data?.forEach((fruits, index) => contentToExport[4].push(
        this.buildConsumptionAndWorkCharts(this.content[2].categories[index], machineData.name, fruits, this.LAST_DAY)));
    });
    this.content[3].data?.forEach(machineData => {
      machineData.data?.forEach((fruits, index) => contentToExport[5].push(
        this.buildConsumptionAndWorkCharts(this.content[3].categories[index], machineData.name, fruits, this.LAST_WEEK)));
    });
    this.content[4].data?.forEach(machineData => {
      machineData.data?.forEach((fruits, index) => contentToExport[6].push(
        this.buildConsumptionAndWorkCharts(this.content[4].categories[index], machineData.name, fruits, this.LAST_YEAR)));
    });
    this.createConsumptionAndWorkSheet(contentToExport, xlsx);
  }

  private exportMachinePark(xlsx: any): void {
    let contentToExport: any[] = [];
    if (this.exportFileType === ExportFileType.ANALYSIS_DISTRIBUTION_BY_COUNTRY_CHART) {
      this.content.forEach(distributionByCountry =>
        contentToExport.push(this.buildDistributionByCountryData(distributionByCountry)));
    } else if (this.exportFileType === ExportFileType.ANALYSIS_DISTRIBUTION_BY_STATE_CHART) {
      this.content.forEach(distributionByState =>
        contentToExport.push(this.buildDistributionByStateData(distributionByState)));
    } else if (this.exportFileType === ExportFileType.ANALYSIS_DISTRIBUTION_BY_LOCALITY_CHART) {
      this.content.forEach(distributionByLocality =>
        contentToExport.push(this.buildDistributionByLocalityData(distributionByLocality)));
    } else if (this.exportFileType === ExportFileType.ANALYSIS_MACHINES_BY_ANTIQUITY_CHART) {
      this.content.forEach((machinesByAntiquity, index) =>
        contentToExport.push(this.buildMachinesByAntiquityData(this.categories[index], machinesByAntiquity)));
    } else if (this.exportFileType === ExportFileType.ANALYSIS_MACHINES_BY_MODEL_CHART) {
      this.content.forEach(machinesByModel => contentToExport.push(this.buildMachinesByModelData(machinesByModel)));
    }
    this.createSheet(contentToExport, xlsx);
  }

  private buildMachineData(machine: Machine) {
    let machineData = {
      [this.i18n.translate('attribute.serialNumber')]: machine.serial,
      [this.i18n.translate('attribute.model')]: machine.model.modelName,
      [this.i18n.translate('attribute.localityState')]: machine.geocoding.locality[this.language]
      + ' (' + machine.geocoding.state[this.language] + ', ' + machine.geocoding.country[this.language] + ')',
      [this.i18n.translate('attribute.alias')]: machine.alias,
      [this.i18n.translate('attribute.squeezedFruits')]: machine.telemetryData.totalSqueezedFruits
        ? machine.telemetryData.totalSqueezedFruits : 0,
      [this.i18n.translate('attribute.state')]: machine.telemetryData.isOn ?
        this.i18n.translate('attribute.connected') :
        this.i18n.translate('attribute.disconnected')
    }
    if (this.authGuard.hasPermission('PERMISSION_VIEW_ALARMS')) {
      machineData[this.i18n.translate('misc.alarms')] = (machine.telemetryData.hasAlarms && machine.telemetryData.activeAlarms?.length > 0) ?
        machine.telemetryData.activeAlarms.map(alarm => alarm.name).join(', ') : '';
    }
    machineData[this.i18n.translate('model.organization')] = machine.organization.name +
      ' (' + machine.organization.orgCode + ')';
    if (this.authGuard.hasPermission('PERMISSION_MANAGE_MACHINES')) {
      machineData[this.i18n.translate('attribute.groups')] = (machine.groups?.length > 0) ?
        machine.groups.map(group => group.name).join(', ') : '';
    }
    return machineData;
  }

  private buildMachineAnalysisData(machine: MachineAnalysis) {
    return {
      [this.i18n.translate('attribute.serialNumber')]: machine.serial,
      [this.i18n.translate('attribute.alias')]: machine.alias,
      [this.i18n.translate('model.organization')]: machine.organization.name +
      ' (' + machine.organization.orgCode + ')',
      [this.i18n.translate('attribute.squeezedFruits')]: machine.totalSqueezedFruits
        ? machine.totalSqueezedFruits : 0
    };
  }

  private buildMachineAnalysisConsumptionData(totalSqueezedFruits: number) {
    return {
      [this.i18n.translate('misc.overview.totalConsumption')]: totalSqueezedFruits
    };
  }

  private buildAlarmData(alarm: Alarm) {
    return {
      [this.i18n.translate('attribute.name')]: alarm.alarmDefinitionName,
      [this.i18n.translate('attribute.status')]: alarm.deactivationDate ?
        this.i18n.translate('attribute.statusInactive') :
        this.i18n.translate('attribute.statusActive'),
      [this.i18n.translate('attribute.alarmType')]: this.i18n.translate('common.' +
        alarm.alarmType.toLowerCase()),
      [this.i18n.translate('attribute.condition')]: this.i18n.translate('common.operation' +
        alarm.operation) + ' (' + alarm.value + ')',
      [this.i18n.translate('attribute.timer')]: alarm.timeWindow ? alarm.timeWindow : '',
      [this.i18n.translate('attribute.activationDate')]: moment(alarm.activationDate).tz(this.timezoneService.getCurrentTimezone()).format(),
      [this.i18n.translate('attribute.deactivationDate')]: alarm.deactivationDate ? moment(alarm.deactivationDate).tz(this.timezoneService.getCurrentTimezone()).format() : '',
      [this.i18n.translate('attribute.duration')]: this.calculateDurationExcel(alarm.activationDate, alarm.deactivationDate),
      [this.i18n.translate('attribute.resetBy')]: alarm.resetUser ? alarm.resetUser.username : ''
    };
  }

  private calculateDurationExcel(activationDate: string, deactivationDate: string): string {
    const daysTranslation: string = (this.i18n.translate('time.days:short'));
    const hoursTranslation: string = this.i18n.translate('time.hours:short');
    const minutesTranslation: string = this.i18n.translate('time.minutes:short');
    const secondsTranslation: string = this.i18n.translate('time.seconds:short');
    return this.timezoneService.calculateDurationExcel(activationDate, deactivationDate, daysTranslation, hoursTranslation,
      minutesTranslation, secondsTranslation);
  }

  private buildOnVsOffData(onVsOff: any) {
    return {
      [this.i18n.translate('misc.overview.onVsOffLastWeek')]: onVsOff.name,
      [this.i18n.translate('attribute.duration')]: onVsOff.custom.time
    };
  }

  private buildAlarmedVsNotAlarmedData(alarmedVsNotAlarmed: any) {
    return {
      [this.i18n.translate('misc.overview.alarmedVsNotAlarmedLastWeek')]: alarmedVsNotAlarmed.name,
      [this.i18n.translate('attribute.duration')]: alarmedVsNotAlarmed.custom.time
    };
  }

  private buildAlarmDistributionData(alarmDistribution: any) {
    return {
      [this.i18n.translate('misc.overview.alarmType')]: alarmDistribution.name,
      [this.i18n.translate('misc.alarms')]: alarmDistribution.y
    };
  }

  private buildAuditData(audit: Audit) {
    return {
      [this.i18n.translate('attribute.attributeName')]: audit.attributeName ? audit.attributeName + ' (' +
        this.i18n.translate('attribute.' + audit.attributeName) + ')' : '',
      [this.i18n.translate('attribute.newValue')]: this.getAttributeValue(audit.newValue),
      [this.i18n.translate('attribute.oldValue')]: this.getAttributeValue(audit.oldValue),
      [this.i18n.translate('attribute.action')]: this.i18n.translate('audits.action.' + audit.action),
      [this.i18n.translate('attribute.user')]: audit.username,
      [this.i18n.translate('attribute.organization')]: audit.organizationName,
      [this.i18n.translate('attribute.auditDate')]: moment(audit.timestamp).tz(this.timezoneService.getCurrentTimezone()).format(),
    };
  }

  private getAttributeValue(value: any): string {
    if (value != undefined) {
      if (value.hasOwnProperty(this.language)) return value[this.language];
      else return value;
    }
    return '';
  }

  private buildOrganizationData(organization: any) {
    let organizationData = {
      [this.i18n.translate('attribute.name')]: organization.name,
      [this.i18n.translate('attribute.orgCode')]: organization.orgCode,
      [this.i18n.translate('attribute.parentName')]: organization.parentUuid ?
        this.content[organization.parentUuid].data.name : '',
      [this.i18n.translate('attribute.parentCode')]: organization.parentUuid ?
        this.content[organization.parentUuid].data.orgCode : '',
      [this.i18n.translate('attribute.description')]: organization.description ? organization.description : '',
      [this.i18n.translate('attribute.organizationType')]: organization.organizationType.name
    }
    if (!this.authGuard.isECommerce()) {
      organizationData[this.i18n.translate('attribute.numberOfMachines')] = organization.totalMachines ? organization.totalMachines : 0;
      organizationData[this.i18n.translate('attribute.totalSqueezedFruits')] = organization.totalSqueezedFruits;
    }
    return organizationData;
  }

  private buildConsumptionAndWorkLastValues(consumptionData: ConsumptionData) {
    return {
      [this.i18n.translate('misc.overview.squeezedFruitsLastHour')]: (consumptionData.squeezedFruitsLastHour == null
        || consumptionData.squeezedFruitsLastHour < 0) ? '' : consumptionData.squeezedFruitsLastHour,
      [this.i18n.translate('misc.overview.squeezedFruitsLastDay')]: (consumptionData.squeezedFruitsLastDay == null
        || consumptionData.squeezedFruitsLastDay < 0) ? '' : consumptionData.squeezedFruitsLastDay,
      [this.i18n.translate('misc.overview.squeezedFruitsLastMonth')]: (consumptionData.squeezedFruitsLastMonth == null
        || consumptionData.squeezedFruitsLastMonth < 0) ? '' : consumptionData.squeezedFruitsLastMonth
    };
  }

  private buildConsumptionAndWorkData(consumptionData: ConsumptionData) {
    return {
      [this.i18n.translate('misc.overview.totalSqueezedFruitsInPeriod')]: (consumptionData.totalSqueezedFruits == null
        || consumptionData.totalSqueezedFruits < 0) ? '' : consumptionData.totalSqueezedFruits,
      [this.i18n.translate('misc.overview.squeezedFruitsAveragePerMachine')]: (consumptionData.squeezedFruitsAveragePerMachine == null
        || consumptionData.squeezedFruitsAveragePerMachine < 0) ? '' : consumptionData.squeezedFruitsAveragePerMachine,
      [this.i18n.translate('misc.overview.squeezedFruitsByServiceHour')]: (consumptionData.squeezedFruitsAveragePerHour == null
        || consumptionData.squeezedFruitsAveragePerHour < 0) ? '' : consumptionData.squeezedFruitsAveragePerHour
    };
  }

  private buildConsumptionAndWorkData2(consumptionData: ConsumptionData) {
    return {
      [this.i18n.translate('misc.overview.squeezedFruitsByDayAndMachine')]: (consumptionData.squeezedFruitsAveragePerDayAndMachine == null
        || consumptionData.squeezedFruitsAveragePerDayAndMachine < 0) ? '' : consumptionData.squeezedFruitsAveragePerDayAndMachine,
      [this.i18n.translate('misc.overview.totalSqueezedFruitsActivation')]: (consumptionData.totalSqueezedFruitsActivation == null
        || consumptionData.totalSqueezedFruitsActivation < 0) ? '' : consumptionData.totalSqueezedFruitsActivation
    };
  }

  private buildDistributionByCountryData(distributionByCountry: any) {
    return {
      [this.i18n.translate('attribute.country')]: distributionByCountry.name,
      [this.i18n.translate('attribute.numberOfMachines')]: distributionByCountry.y
    };
  }

  private buildDistributionByStateData(distributionByState: any) {
    return {
      [this.i18n.translate('attribute.provinceState')]: distributionByState.name,
      [this.i18n.translate('attribute.numberOfMachines')]: distributionByState.y
    };
  }

  private buildDistributionByLocalityData(distributionByLocality: any) {
    return {
      [this.i18n.translate('attribute.locality')]: distributionByLocality.name,
      [this.i18n.translate('attribute.numberOfMachines')]: distributionByLocality.y
    };
  }

  private buildMachinesByAntiquityData(category: string, totalMachines: number) {
    return {
      [this.i18n.translate('attribute.machineAge')]: category,
      [this.i18n.translate('attribute.numberOfMachines')]: totalMachines
    };
  }

  private buildMachinesByModelData(machinesByModel: any) {
    return {
      [this.i18n.translate('attribute.machineModel')]: machinesByModel.name,
      [this.i18n.translate('attribute.numberOfMachines')]: machinesByModel.y
    };
  }

  private buildFruitsMachineData(serial: string, fruitsMachine: number[]) {
    return {
      [this.i18n.translate('attribute.serialNumber')]: serial,
      [this.i18n.translate('attribute.date')]: moment(fruitsMachine[0]).tz(this.timezoneService.getCurrentTimezone()).format(),
      [this.i18n.translate('attribute.squeezedFruits')]: fruitsMachine[1]
    };
  }

  private buildMachineGeneralUpperData(machine: Machine) {
    return {
      [this.i18n.translate('attribute.serialNumber')]: machine.serial,
      [this.i18n.translate('attribute.alias')]: machine.alias,
      [this.i18n.translate('attribute.model')]: machine.model.modelName,
      [this.i18n.translate('model.organization')]: machine.organization.name + ' (' + machine.organization.orgCode + ')',
      [this.i18n.translate('misc.overview.geolocation')]: machine.geocoding.locality[this.language]
      + ' (' + machine.geocoding.state[this.language] + ', ' + machine.geocoding.country[this.language] + ')'
    };
  }

  private buildMachineGeneralValuesData(machine: Machine, machineGeneralInfo: MachineGeneralInfo, machineWorkDailyCounter: string) {
    return {
      [this.i18n.translate(this.viewInLiters ? 'misc.overview.totalSqueezedLitersCounter' : 'misc.overview.totalSqueezedFruitsCounter')]:
        this.viewInLiters ? machine.telemetryData.totalSqueezedLiters : machine.telemetryData.totalSqueezedFruits,
      [this.i18n.translate(this.viewInLiters ? 'misc.overview.partialSqueezedLitersCounter' : 'misc.overview.partialSqueezedFruitsCounter')]: machineGeneralInfo.partialSqueezedValuesCounter,
      [this.i18n.translate('attribute.lastValueReported')]: moment(machine.telemetryData.lastValueReported).tz(this.timezoneService.getCurrentTimezone()).format(),
      [this.i18n.translate('misc.overview.workDailyCounter')]: machineWorkDailyCounter
    };
  }

  private buildMachineGeneralInfoData(machine: Machine) {
    return {
      [this.i18n.translate('misc.overview.telemetryKitNumber')]: machine.telemetryData.integrationIdentifier ?
        machine.telemetryData.integrationIdentifier : this.EMPTY_DATA,
      [this.i18n.translate('attribute.activationDate')]: moment(machine.manufacturingDate).tz(this.timezoneService.getCurrentTimezone()).format(),
      [this.i18n.translate('attribute.communicationType')]: machine.telemetryData.communicationType != undefined ?
        this.getCommunicationTypeTranslation(machine) : this.EMPTY_DATA,
      [this.i18n.translate('attribute.automaticGeoloc')]: machine.automaticGeoloc ? this.i18n.translate('attribute.yes') :
        this.i18n.translate('attribute.no'),
      [this.i18n.translate('attribute.descriptionModel')]: machine.model.description,
      [this.i18n.translate('attribute.deleted')]: machine.deleted ? this.i18n.translate('attribute.yes') :
        this.i18n.translate('attribute.no')
    };
  }

  private getCommunicationTypeTranslation(machine: Machine): string {
    return machine.telemetryData.communicationType === 0 ? this.i18n.translate('attribute.gprs') :
      this.i18n.translate('attribute.wifi');
  }

  private buildMachineGeneralStatusData(machine: Machine, machineGeneralInfo: MachineGeneralInfo) {
    let workHours;
    if (!this.authGuard.isECommerce()) {
      workHours = machine.telemetryData.workHours != null ?
        machine.telemetryData.workHours - machine.telemetryData.initialWorkHours : this.EMPTY_DATA;
    } else {
      workHours = machineGeneralInfo.subscriptionWorkHours ?? this.EMPTY_DATA;
    }
    const isOnTranslation: string = machine.telemetryData.isPoweredOn ? this.i18n.translate('attribute.isOn') : this.EMPTY_DATA;
    return {
      [this.i18n.translate('misc.overview.workingHours')]: workHours,
      [this.i18n.translate('attribute.machineAge')]: machine.telemetryData.machineAge ?? this.EMPTY_DATA,
      [this.i18n.translate('attribute.powerOnTime')]: machine.telemetryData.powerOnTime ?
        moment(machine.telemetryData.powerOnTime).tz(this.timezoneService.getCurrentTimezone()).format() : this.EMPTY_DATA,
      [this.i18n.translate('attribute.powerOffTime')]: (!machine.telemetryData.isPoweredOn && machine.telemetryData.powerOffTime) ?
        moment(machine.telemetryData.powerOffTime).tz(this.timezoneService.getCurrentTimezone()).format() : isOnTranslation,
    };
  }

  private buildMachineGeneralStatusButtons(machine: Machine) {
    let data = {
      [this.i18n.translate('attribute.status')]: machine.telemetryData.isOn ?
        this.i18n.translate('attribute.connected') : this.i18n.translate('attribute.disconnected'),
      [this.i18n.translate('attribute.switchStatus')]: machine.telemetryData.isPoweredOn ?
        this.i18n.translate('attribute.on') : this.i18n.translate('attribute.off'),
      [this.i18n.translate('misc.alarms')]: machine.telemetryData.hasAlarms ?
        this.i18n.translate('attribute.hasAlarms') : this.i18n.translate('attribute.noAlarms')
    };
    if (this.authGuard.hasPermission('PERMISSION_MANAGE_ADVANCED_MACHINES')) {
      data[this.i18n.translate('misc.events')] = machine.telemetryData.hasEvents ?
        this.i18n.translate('attribute.hasEvents') : this.i18n.translate('attribute.noEvents')
    }
    return data;
  }

  private buildMachineActiveAlarmsData(alarm: AlarmTelemetryData) {
    return {
      [this.i18n.translate('attribute.name')]: alarm.name,
      [this.i18n.translate('attribute.activationDate')]: moment(alarm.activationDate).tz(this.timezoneService.getCurrentTimezone()).format()
    };
  }

  private buildMachineLastValuesData(machineLastValues: MachineLastValues) {
    return {
      [this.i18n.translate(this.viewInLiters ? 'misc.overview.squeezedLitersLastHour' : 'misc.overview.squeezedFruitsLastHour')]:
        (machineLastValues.squeezedValuesLastHour == null || machineLastValues.squeezedValuesLastHour < 0) ? '' : machineLastValues.squeezedValuesLastHour,
      [this.i18n.translate(this.viewInLiters ? 'misc.overview.squeezedLitersLastDay' : 'misc.overview.squeezedFruitsLastDay')]:
        (machineLastValues.squeezedValuesLastDay == null || machineLastValues.squeezedValuesLastDay < 0) ? '' : machineLastValues.squeezedValuesLastDay,
      [this.i18n.translate(this.viewInLiters ? 'misc.overview.squeezedLitersLastMonth' : 'misc.overview.squeezedFruitsLastMonth')]:
        (machineLastValues.squeezedValuesLastMonth == null || machineLastValues.squeezedValuesLastMonth < 0) ? '' : machineLastValues.squeezedValuesLastMonth
    };
  }

  private buildTimePeriod(periodRange: [number, number]) {
    return {
      [this.i18n.translate('attribute.startDate')]: moment(periodRange[0]).tz(this.timezoneService.getCurrentTimezone()).format(),
      [this.i18n.translate('attribute.endDate')]: moment(periodRange[1]).tz(this.timezoneService.getCurrentTimezone()).format()
    };
  }

  private buildMachinePeriodData(machinePeriodDataInfo: MachinePeriodDataInfo) {
    return {
      [this.i18n.translate(this.filtersViewInLiters ? 'misc.overview.totalSqueezedLitersInPeriod' : 'misc.overview.totalSqueezedFruitsInPeriod')]:
        (machinePeriodDataInfo.totalSqueezedValues == null || machinePeriodDataInfo.totalSqueezedValues < 0) ? '' : machinePeriodDataInfo.totalSqueezedValues,
      [this.i18n.translate(this.filtersViewInLiters ? 'misc.overview.squeezedLitersByDay' : 'misc.overview.squeezedFruitsByDay')]:
        (machinePeriodDataInfo.squeezedValuesAveragePerDay == null || machinePeriodDataInfo.squeezedValuesAveragePerDay < 0) ? '' : machinePeriodDataInfo.squeezedValuesAveragePerDay,
      [this.i18n.translate(this.filtersViewInLiters ? 'misc.overview.squeezedLitersByServiceHour' : 'misc.overview.squeezedFruitsByServiceHour')]:
        (machinePeriodDataInfo.squeezedValuesAveragePerHour == null || machinePeriodDataInfo.squeezedValuesAveragePerHour < 0) ? '' : machinePeriodDataInfo.squeezedValuesAveragePerHour
    };
  }

  private buildSignalData(signal: string, signalValues: [number, number]) {
    return {
      [this.i18n.translate('attribute.electricSignal')]: signal,
      [this.i18n.translate('attribute.date')]: moment(signalValues[0]).tz(this.timezoneService.getCurrentTimezone()).format(),
      [this.i18n.translate('attribute.value')]: signalValues[1]
    };
  }

  private buildMachineLastValuesCharts(category: string, squeezedValues: number, chartType: string) {
    let categoryColumnName;
    if (chartType === this.LAST_DAY) {
      categoryColumnName = this.i18n.translate('time.hourOfLastDay');
    } else if (chartType === this.LAST_WEEK) {
      categoryColumnName = this.i18n.translate('time.dayOfLastWeek');
    } else {
      categoryColumnName = this.i18n.translate('time.monthOfLastYear');
    }
    return {
      [categoryColumnName]: category,
      [this.i18n.translate(this.viewInLiters ? 'attribute.squeezedLiters' : 'attribute.squeezedFruits')]: squeezedValues
    };
  }

  private buildConsumptionAndWorkCharts(category: string, serial: string, squeezedFruits: number, chartType: string) {
    let categoryColumnName;
    if (chartType === this.LAST_DAY) {
      categoryColumnName = this.i18n.translate('time.hourOfLastDay');
    } else if (chartType === this.LAST_WEEK) {
      categoryColumnName = this.i18n.translate('time.dayOfLastWeek');
    } else {
      categoryColumnName = this.i18n.translate('time.monthOfLastYear');
    }
    return {
      [this.i18n.translate('attribute.serialNumber')]: serial,
      [categoryColumnName]: category,
      [this.i18n.translate('attribute.squeezedFruits')]: squeezedFruits
    };
  }

  private buildMachineLastValuesPeriodChartsData(operatingTime: string, outOfServiceTime: string) {
    return {
      [this.i18n.translate('misc.overview.machineOperatingTime')]: operatingTime || '',
      [this.i18n.translate('misc.overview.machineOutOfServiceTime')]: outOfServiceTime || ''
    };
  }

  private buildValuesMeasurement(totalSqueezedMeasurement: [number, number], viewInLiters: boolean) {
    return {
      [this.i18n.translate('attribute.date')]: moment(totalSqueezedMeasurement[0]).tz(this.timezoneService.getCurrentTimezone()).format(),
      [this.i18n.translate(viewInLiters ? 'attribute.squeezedLiters' : 'attribute.squeezedFruits')]: totalSqueezedMeasurement[1]
    }
  }

  createSheet(content: any[], xlsx: any): void {
    const worksheet = xlsx.utils.json_to_sheet(content);
    if (this.typeExport === 'XLSX') {
      this.saveWorkbook(worksheet, content, xlsx);
    } else {
      const csvWorksheet = xlsx.utils.sheet_to_csv(worksheet, {FS: ';'});
      this.saveAsFile(csvWorksheet, this.fileName, FileExtensionType.CSV);
      this.auditsService.createDownloadAudit(this.fileName.trim() + '.csv', csvWorksheet.length, this.exportFileType,
        FileExtensionType.CSV, this.machineSerialNumber).subscribe();
    }
    this.loadingContent = false;
  }

  saveWorkbook(worksheet: any, content: any[], xlsx: any): void {
    const workbook = {Sheets: {'data': worksheet}, SheetNames: ['data']};
    worksheet['!cols'] = this.fixColsWidth(content);
    const excelBuffer: any = xlsx.write(workbook, {bookType: 'xlsx', type: 'array'});
    this.auditsService.createDownloadAudit(this.fileName.trim() + '.xlsx', excelBuffer.byteLength, this.exportFileType,
      FileExtensionType.EXCEL, this.machineSerialNumber).subscribe();
    this.saveAsFile(excelBuffer, this.fileName, FileExtensionType.EXCEL);
  }

  createMachineGeneralSheet(content: any[], xlsx: any): void {
    const worksheet = xlsx.utils.json_to_sheet(content[0]);
    xlsx.utils.sheet_add_json(worksheet, content[1], {origin: {r: 3, c: 0}});
    xlsx.utils.sheet_add_json(worksheet, content[2], {origin: {r: 6, c: 0}});
    let startNextRow: number = 9;
    xlsx.utils.sheet_add_json(worksheet, content[3], {origin: {r: startNextRow, c: 0}});
    startNextRow = content[3].length == 0 ? startNextRow : startNextRow + content[3].length + 2;
    xlsx.utils.sheet_add_json(worksheet, content[4], {origin: {r: startNextRow, c: 0}});
    startNextRow = content[4].length == 0 ? startNextRow : startNextRow + content[4].length + 2;
    xlsx.utils.sheet_add_json(worksheet, content[5], {origin: {r: startNextRow, c: 0}});
    startNextRow = content[5].length == 0 ? startNextRow : startNextRow + content[5].length + 2;
    xlsx.utils.sheet_add_json(worksheet, content[6], {origin: {r: startNextRow, c: 0}});
    let startNextColumn = content[6].length == 0 ? 0 : 2;
    xlsx.utils.sheet_add_json(worksheet, content[7], {origin: {r: startNextRow, c: startNextColumn}});
    startNextColumn = content[7].length == 0 ? startNextColumn : startNextColumn + 2;
    xlsx.utils.sheet_add_json(worksheet, content[8], {origin: {r: startNextRow, c: startNextColumn}});
    this.saveWorkbook(worksheet, content, xlsx);
    this.loadingContent = false;
  }

  createMachineDataSheet(content: any[], xlsx: any): void {
    const worksheet = xlsx.utils.json_to_sheet([content[0]]);
    xlsx.utils.sheet_add_json(worksheet, [content[1]], {origin: {r: 3, c: 0}});
    xlsx.utils.sheet_add_json(worksheet, [content[2]], {origin: {r: 6, c: 0}});
    this.saveWorkbook(worksheet, content, xlsx);
    this.loadingContent = false;
  }

  createMachineChartsSheet(content: any[], xlsx: any): void {
    const worksheet = xlsx.utils.json_to_sheet(content[0]);
    let secondTableStart = content[0].length === 0 ? 0 : 3;
    xlsx.utils.sheet_add_json(worksheet, content[1], {origin: {r: 0, c: secondTableStart}});
    xlsx.utils.sheet_add_json(worksheet, content[2], {
      origin: {
        r: 0,
        c: content[1].length === 0 ? secondTableStart : secondTableStart + 3
      }
    });
    this.saveWorkbook(worksheet, content, xlsx);
    this.loadingContent = false;
  }

  createMachineChartsPeriodSheet(content: any[], xlsx: any): void {
    const worksheet = xlsx.utils.json_to_sheet(content[0]);
    xlsx.utils.sheet_add_json(worksheet, content[1], {origin: {r: 3, c: 0}});
    xlsx.utils.sheet_add_json(worksheet, content[2], {origin: {r: 6, c: 0}});
    this.saveWorkbook(worksheet, content, xlsx);
    this.loadingContent = false;
  }

  createAnalysisGeneralSheet(content: any[], xlsx: any): void {
    const worksheet = xlsx.utils.json_to_sheet([content[0]]);
    xlsx.utils.sheet_add_json(worksheet, content.slice(1), {origin: {r: 3, c: 0}});
    this.saveWorkbook(worksheet, content, xlsx);
    this.loadingContent = false;
  }

  createConsumptionAndWorkSheet(content: any[], xlsx: any): void {
    const worksheet = xlsx.utils.json_to_sheet(content[0]);
    xlsx.utils.sheet_add_json(worksheet, content[1], {origin: {r: 3, c: 0}});
    xlsx.utils.sheet_add_json(worksheet, content[2], {origin: {r: 6, c: 0}});
    xlsx.utils.sheet_add_json(worksheet, content[3], {origin: {r: 9, c: 0}});
    let startNextRow = content[1].length === 0 ? 4 : 13;
    xlsx.utils.sheet_add_json(worksheet, content[4], {origin: {r: startNextRow, c: 0}});
    startNextRow = content[4].length == 0 ? 13 : 13 + content[4].length + 3;
    xlsx.utils.sheet_add_json(worksheet, content[5], {origin: {r: startNextRow, c: 0}});
    startNextRow = content[5].length == 0 ? startNextRow : startNextRow + content[5].length + 3;
    xlsx.utils.sheet_add_json(worksheet, content[6], {origin: {r: startNextRow, c: 0}});
    this.saveWorkbook(worksheet, content, xlsx);
    this.loadingContent = false;
  }

  fixColsWidth(content: any[]): ColInfo[] {
    switch (this.typeContent) {
      case 'MACHINE':
        return this.fixColsWidthMachines(content);
      case 'MACHINE_GENERAL':
        return this.fixColsWidthMachineGeneral(content);
      case 'MACHINE_DATA':
        return this.fixColsWidthMachineData(content);
      case 'MACHINE_CHARTS':
        return this.fixColsWidthMachineCharts(content);
      case 'ALARM':
        return this.fixColsWidthAlarms(content);
      case 'AUDIT':
        return this.fixColsWidthAudits(content);
      case 'MACHINE_ANALYSIS':
        return this.fixColsWidthAnalysisGeneral(content);
      case 'ORGANIZATION':
        return this.fixColsWidthOrganizations(content);
      case 'CONSUMPTION':
        return this.fixColsWidthAnalysisConsumption(content);
      case 'MACHINE_PARK':
        return this.fixColsWidthMachinePark(content);
    }
  }

  private fixColsWidthMachines(content: any[]): ColInfo[] {
    let colsWidth: ColInfo[] = [];
    let col: number = 0;
    colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.serialNumber')].length + 2), 15)};
    colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.model')].length), 10)};
    colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.localityState')].length), 10)};
    colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.alias')].length), 10)};
    colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.squeezedFruits')]?.toString().length), 15)};
    colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.state')].length + 1), 10)};
    if (this.authGuard.hasPermission('PERMISSION_VIEW_ALARMS')) {
      colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('misc.alarms')] ? r[this.i18n.translate('misc.alarms')].length : 0), 10)};
    }
    colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('model.organization')].length + 2), 10)};
    if (this.authGuard.hasPermission('PERMISSION_MANAGE_MACHINES')) {
      colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.groups')] ? r[this.i18n.translate('attribute.groups')].length : 0), 10)};
    }
    return colsWidth;
  }

  private fixColsWidthMachineGeneral(content: any[]): ColInfo[] {
    let colsWidth: ColInfo[] = [];
    let col: number = 0;
    colsWidth[col++] = {wch: content.reduce(w => Math.max(w, 15), 25)};
    colsWidth[col++] = {wch: content.reduce(w => Math.max(w, 15), 25)};
    colsWidth[col++] = {wch: content.reduce(w => Math.max(w, 15), 25)};
    colsWidth[col++] = {wch: content.reduce(w => Math.max(w, 15), 25)};
    colsWidth[col++] = {wch: content.reduce(w => Math.max(w, 15), 25)};
    colsWidth[col++] = {wch: content.reduce(w => Math.max(w, 15), 25)};
    return colsWidth;
  }

  private fixColsWidthMachineData(content: any[]): ColInfo[] {
    if (this.exportFileType === ExportFileType.MACHINE_DATA_CHART) {
      return this.fixColsWidthMachineDataSignals(content);
    } else if (this.exportFileType === ExportFileType.MACHINE_DATA) {
      return this.fixColsWidthMachineDataLastValuesPeriodData(content);
    }
  }

  private fixColsWidthMachineDataSignals(content: any[]): ColInfo[] {
    let colsWidth: ColInfo[] = [];
    let col: number = 0;
    colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.electricSignal')].length + 2), 10)};
    colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.date')].length), 10)};
    colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.value')]?.toString().length + 2), 10)};
    return colsWidth;
  }

  private fixColsWidthMachineDataLastValuesPeriodData(content: any[]): ColInfo[] {
    let colsWidth: ColInfo[] = [];
    let col: number = 0;
    colsWidth[col++] = {wch: content.reduce(w => Math.max(w, 15), 40)};
    colsWidth[col++] = {wch: content.reduce(w => Math.max(w, 15), 40)};
    colsWidth[col++] = {wch: content.reduce(w => Math.max(w, 15), 40)};
    return colsWidth;
  }

  private fixColsWidthMachineCharts(content: any[]): ColInfo[] {
    if (this.exportFileType === ExportFileType.MACHINE_CHARTS) {
      return this.fixColsWidthLastValuesCharts(content);
    } else if (this.exportFileType === ExportFileType.MACHINE_CHARTS_IN_PERIOD) {
      return this.fixColsWidthLastValuesPeriodCharts(content);
    }
  }

  private fixColsWidthLastValuesCharts(content: any[]): ColInfo[] {
    let colsWidth: ColInfo[] = [];
    let col: number = 0;
    let numberOfTables: number = 0;
    content.forEach(element => {
      if (element.length > 0) {
        numberOfTables++;
      }
    });
    if (numberOfTables > 0) {
      colsWidth[0] = {wch: content.reduce(w => Math.max(w, 15), 20)};
      colsWidth[1] = {wch: content.reduce(w => Math.max(w, 15), 15)};
      if (numberOfTables > 1) {
        colsWidth[3] = {wch: content.reduce(w => Math.max(w, 15), 20)};
        colsWidth[4] = {wch: content.reduce(w => Math.max(w, 15), 15)};
        if (numberOfTables > 2) {
          colsWidth[6] = {wch: content.reduce(w => Math.max(w, 15), 20)};
          colsWidth[7] = {wch: content.reduce(w => Math.max(w, 15), 15)};
        }
      }
    }
    return colsWidth;
  }

  private fixColsWidthLastValuesPeriodCharts(content: any[]): ColInfo[] {
    let colsWidth: ColInfo[] = [];
    let col: number = 0;
    colsWidth[col++] = {wch: content.reduce(w => Math.max(w, 15), 35)};
    colsWidth[col++] = {wch: content.reduce(w => Math.max(w, 15), 30)};
    return colsWidth;
  }

  private fixColsWidthAlarms(content: any[]): ColInfo[] {
    let colsWidth: ColInfo[] = [];
    let col: number = 0;
    if (this.exportFileType === ExportFileType.MACHINE_ALARMS_LIST) {
      colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.name')].length + 2), 10)};
      colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.status')].length), 10)};
      colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.alarmType')].length), 14)};
      colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.condition')].length), 14)};
      colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.timer')]?.toString().length + 10), 12)};
      colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.activationDate')].length), 10)};
      colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.deactivationDate')]?.length), 10)};
      colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.duration')].length), 10)};
      colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.resetBy')]?.length + 2), 15)};
    } else if (this.exportFileType === ExportFileType.MACHINE_ALARMS_TIME_ON_VS_OFF_CHART) {
      colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('misc.overview.onVsOffLastWeek')].length + 2), 38)};
      colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.duration')]?.length + 2), 10)};
    } else if (this.exportFileType === ExportFileType.MACHINE_ALARMS_TIME_CHART) {
      colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('misc.overview.alarmedVsNotAlarmedLastWeek')].length + 2), 38)};
      colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.duration')]?.length + 2), 10)};
    } else if (this.exportFileType === ExportFileType.MACHINE_ALARMS_DISTRIBUTION_CHART) {
      colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('misc.overview.alarmType')].length + 2), 10)};
      colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('misc.alarms')]?.toString().length + 2), 10)};
    }
    return colsWidth;
  }

  private fixColsWidthAudits(content: any[]): ColInfo[] {
    let colsWidth: ColInfo[] = [];
    let col: number = 0;
    colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.attributeName')]?.length), 10)};
    colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.newValue')]?.length), 10)};
    colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.oldValue')]?.length), 10)};
    colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.action')].length), 10)};
    colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.user')].length + 2), 10)};
    colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.organization')]?.length + 2), 12)};
    colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.auditDate')].length), 10)};
    return colsWidth;
  }

  private fixColsWidthAnalysisGeneral(content: any[]): ColInfo[] {
    let colsWidth: ColInfo[] = [];
    let col: number = 0;
    colsWidth[col++] = {
      wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.serialNumber')] != null ?
        r[this.i18n.translate('attribute.serialNumber')].length + 2 : r[this.i18n.translate('misc.overview.totalConsumption')]?.toString().length + 2), 20)
    };
    colsWidth[col++] = {
      wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.alias')] != null ?
        r[this.i18n.translate('attribute.alias')].length : 2), 10)
    };
    colsWidth[col++] = {
      wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('model.organization')] != null ?
        r[this.i18n.translate('model.organization')].length + 2 : 2), 10)
    };
    colsWidth[col++] = {
      wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.squeezedFruits')] != null ?
        r[this.i18n.translate('attribute.squeezedFruits')]?.toString().length : 2), 15)
    };
    return colsWidth;
  }

  private fixColsWidthOrganizations(content: any[]): ColInfo[] {
    let colsWidth: ColInfo[] = [];
    let col: number = 0;
    colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.name')].length + 2), 10)};
    colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.orgCode')].length + 2), 10)};
    colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.parentName')].length + 2), 30)};
    colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.parentCode')].length + 2), 30)};
    colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.description')]?.length), 10)};
    colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.organizationType')].length), 18)};
    if (!this.authGuard.isECommerce()) {
      colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.numberOfMachines')]?.toString().length + 2), 18)};
      colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.totalSqueezedFruits')]?.toString().length + 2), 22)};
    }
    return colsWidth;
  }

  private fixColsWidthAnalysisConsumption(content: any[]): ColInfo[] {
    let colsWidth: ColInfo[] = [];
    let col: number = 0;
    if (this.exportFileType === ExportFileType.ANALYSIS_CONSUMPTION_AND_WORK_CHART) {
      colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.serialNumber')].length + 5), 10)};
      colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.date')]?.toString().length), 10)};
      colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.squeezedFruits')]?.toString().length + 4), 15)};
    } else {
      colsWidth[col++] = {wch: content.reduce(w => Math.max(w, 15), 45)};
      colsWidth[col++] = {wch: content.reduce(w => Math.max(w, 15), 40)};
      colsWidth[col++] = {wch: content.reduce(w => Math.max(w, 15), 40)};
    }
    return colsWidth;
  }

  private fixColsWidthMachinePark(content: any[]): ColInfo[] {
    let colsWidth: ColInfo[] = [];
    let col: number = 0;
    if (this.exportFileType === ExportFileType.ANALYSIS_DISTRIBUTION_BY_COUNTRY_CHART) {
      colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.country')].length), 10)};
      colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.numberOfMachines')]?.toString().length + 2), 18)};
    } else if (this.exportFileType === ExportFileType.ANALYSIS_DISTRIBUTION_BY_STATE_CHART) {
      colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.provinceState')].length + 4), 10)};
      colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.numberOfMachines')]?.toString().length + 2), 18)};
    } else if (this.exportFileType === ExportFileType.ANALYSIS_DISTRIBUTION_BY_LOCALITY_CHART) {
      colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.locality')].length), 10)};
      colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.numberOfMachines')]?.toString().length + 2), 18)};
    } else if (this.exportFileType === ExportFileType.ANALYSIS_MACHINES_BY_ANTIQUITY_CHART) {
      colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.machineAge')].length), 10)};
      colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.numberOfMachines')]?.toString().length + 2), 18)};
    } else if (this.exportFileType === ExportFileType.ANALYSIS_MACHINES_BY_MODEL_CHART) {
      colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.machineModel')].length + 4), 10)};
      colsWidth[col++] = {wch: content.reduce((w, r) => Math.max(w, r[this.i18n.translate('attribute.numberOfMachines')]?.toString().length + 2), 18)};
    }
    return colsWidth;
  }

  saveAsFile(buffer: any, fileName: string, fileExtensionType: FileExtensionType): void {
    let extension: string = (fileExtensionType === FileExtensionType.EXCEL) ? '.xlsx' : '.csv';
    const data: Blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
    });
    FileSaver.saveAs(data, fileName.trim() + extension);
    this.visible = false;
    this.visibleChange.emit(false);
  }

  public saveAndClose(): void {
    if (this.fileName && this.fileName.length > 0 && !this.loadingContent) {
      this.loadingContent = true;
      if (this.contentTypeExport === 'currentPage') {
        this.exportCurrentContentToExcel();
      } else {
        this.exportAllContentToExcel();
      }
    }
  }

  public closeWithoutSaving(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  public readonly ExportFileType = ExportFileType;
}
